/** Display formatting. Exact amounts go through units.ts; these helpers format floats for reading. */
import { formatUnits, SOL_DECIMALS } from './units';

const SUBSCRIPT = '₀₁₂₃₄₅₆₇₈₉';
const sub = (n: number) => String(n).replace(/\d/g, (d) => SUBSCRIPT[Number(d)] ?? d);

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** 3 significant digits with K/M/B suffix (pump-style): 2.19K, 40.4K, 291K, 1.76M. */
export function fmtCompact(n: number): string {
  if (!Number.isFinite(n)) return '–';
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  for (const [v, u] of units) {
    if (a >= v * 0.9995) {
      const x = a / v;
      const s = x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x.toFixed(2);
      return `${sign}${trimZeros(s)}${u}`;
    }
  }
  if (a >= 100) return `${sign}${a.toFixed(0)}`;
  if (a >= 1) return `${sign}${trimZeros(a.toFixed(2))}`;
  if (a === 0) return '0';
  return `${sign}${trimZeros(a.toPrecision(3))}`;
}

/** $1.76M, $291K, $212.91, $0.42 */
export function fmtUsd(n: number): string {
  if (!Number.isFinite(n)) return '–';
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a < 1000) return `${sign}$${a >= 1 || a === 0 ? a.toFixed(2) : a.toPrecision(2)}`;
  return `${sign}$${fmtCompact(a)}`;
}

/** A price with 4 significant digits; tiny values use a subscript zero count (0.0₆2795). */
export function fmtNum(n: number, sig = 4): string {
  if (!Number.isFinite(n)) return '–';
  if (n === 0) return '0';
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1000) return sign + a.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (a >= 1) return sign + trimZeros(a.toFixed(2));
  const zeros = Math.floor(-Math.log10(a));
  const digits = Math.round(a * 10 ** (zeros + sig - 1));
  const ds = String(digits).replace(/0+$/, '') || '0';
  if (zeros >= 4) return `${sign}0.0${sub(zeros)}${ds}`;
  return `${sign}0.${'0'.repeat(zeros)}${ds}`;
}

export const fmtPriceUsd = (n: number) => `$${fmtNum(n)}`;

/** SOL with sensible precision: 12.4, 0.5, 0.00125, 0.0₅2795. */
export function fmtSolNum(n: number): string {
  if (!Number.isFinite(n)) return '–';
  const a = Math.abs(n);
  if (a === 0) return '0';
  if (a >= 1000) return fmtCompact(n);
  if (a >= 1) return trimZeros(n.toFixed(a >= 100 ? 1 : 2));
  if (a >= 0.001) return trimZeros(n.toFixed(4));
  return fmtNum(n, 3);
}

export const fmtSol = (n: number) => `${fmtSolNum(n)} SOL`;

/** Exact lamports as SOL ("0.00125"), every significant digit kept. */
export const fmtLamports = (l: bigint) => formatUnits(l, SOL_DECIMALS);

export function fmtPct(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '–';
  const a = Math.abs(n);
  const d = a >= 1000 ? 0 : digits;
  return `${a.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}%`;
}

export function fmtPctSigned(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '–';
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}${fmtPct(n, digits)}`;
}

/** A fee rate in basis points as a percent: 25 -> "0.25%", 0 -> "0%". */
export function fmtBps(bps: number): string {
  return `${trimZeros((bps / 100).toFixed(4))}%`;
}

/** Seconds -> 12s, 5m, 3h, 2d, 4mo, 1y */
export function fmtAge(seconds: number): string {
  if (!Number.isFinite(seconds)) return '–';
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h`;
  if (s < 30 * 86_400) return `${Math.floor(s / 86_400)}d`;
  if (s < 365 * 86_400) return `${Math.floor(s / (30 * 86_400))}mo`;
  return `${Math.floor(s / (365 * 86_400))}y`;
}

export function shortAddr(a: string, head = 4, tail = 4): string {
  if (!a) return '';
  return a.length <= head + tail + 1 ? a : `${a.slice(0, head)}…${a.slice(-tail)}`;
}

/** Whole tokens from base units (6 decimals) as a compact string: 12.3M */
export function fmtTokens(base: bigint | string, decimals = 6): string {
  const b = typeof base === 'string' ? BigInt(base || '0') : base;
  const n = Number(b) / 10 ** decimals;
  return n >= 1000 ? fmtCompact(n) : fmtSolNum(n);
}

export function fmtDateTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
